import type {
  DashboardQuantityBuckets,
  DashboardUnitKind,
} from "./quantityBuckets";

// --- API response types (mirror Go backend) ---

export interface OrderSummary {
  open: number;
  concluido: number;
  carregamento: number;
  change_requested: number;
  encerrado: number;
  cancelled: number;
  total: number;
}

export interface CheckinSummary {
  total: number;
  pending: number;
  finalized: number;
  incongruent: number;
  /** Physical caixas only; carga progress uses total/finalized. */
  total_boxes: number;
  /** Physical caixas only; carga progress uses total/finalized. */
  checked_boxes: number;
  pct_in_progress: number;
}

export interface AlertStatusCounts {
  sent: number;
  seen: number;
  solved: number;
}

export interface AlertPriorityCounts {
  high: number;
  medium: number;
  low: number;
}

export interface AlertSummary {
  by_status: AlertStatusCounts;
  by_priority: AlertPriorityCounts;
}

export interface DashboardOpenLoads {
  not_started: number;
  in_progress: number;
  with_incongruent: number;
  total_value_cents: number;
}

export interface DashboardActions {
  unresolved_alerts: number;
  high_priority_alerts: number;
  pending_change_requests: number;
}

export interface DashboardOperations {
  open_loads: DashboardOpenLoads;
  actions: DashboardActions;
}

export interface DashboardQuantityTotals {
  kg: number;
  cx: number;
  bdj: number;
  sc: number;
  un: number;
}

export interface DashboardCategorySpend {
  category: string;
  value_cents: number;
}

export interface DashboardAnalytics {
  orders: OrderSummary;
  total_value_cents: number;
  quantities: DashboardQuantityTotals;
  category_spend: DashboardCategorySpend[];
  product_volumes: DashboardProductVolume[];
  supplier_values: DashboardSupplierValue[];
  quality: {
    decision: number;
    substituido: number;
    devolvido: number;
  };
}

export interface DashboardProductVolume {
  product_name: string;
  total_value_cents: number;
  quantities: DashboardQuantityTotals;
}

export interface DashboardSupplierValue {
  supplier_name: string;
  total_value_cents: number;
}

export interface DashboardPriceDrop {
  product_name: string;
  supplier_name: string;
  previous_price_cents: number;
  current_price_cents: number;
  percentage_drop: number;
}

export interface DashboardPriceDrops {
  items: DashboardPriceDrop[];
}

export interface SuperSummary {
  orders: OrderSummary;
  checkins: CheckinSummary;
  alerts: AlertSummary;
  pending_change_requests: number;
  total_value: number; // integer cents
  total_commission: number; // integer cents
  total_boxes: number;
}

export interface ClientSummary {
  orders: OrderSummary;
  checkins: CheckinSummary;
  returns: {
    decision: number;
    substituido: number;
    devolvido: number;
  };
  total_value: number; // integer cents
  total_boxes: number;
}

export interface SupplierReturnSummary {
  total_supplied: number;
  total_returns: number;
  return_rate: number;
  financial_impact: number; // integer cents
}

export interface SupplierSummary {
  orders: OrderSummary;
  total_value: number; // integer cents
  total_commission: number; // integer cents
  total_boxes: number;
  returns: SupplierReturnSummary;
}

export interface SupplierReturnRank {
  supplier_id: string;
  supplier_name: string;
  total_supplied: number;
  total_returns: number;
  decision_count: number;
  substituido_count: number;
  devolvido_count: number;
  return_rate: number;
  financial_impact: number;
}

export interface SupplierReturnReport {
  worst: SupplierReturnRank[];
  best: SupplierReturnRank[];
  items: SupplierReturnRank[];
}

export interface ClientRank {
  client_id: string;
  client_name: string;
  total_quantity: number;
  total_value: number;
}

export interface StoreRank {
  store_id: string;
  store_name: string;
  client_id: string;
  client_name: string;
  total_quantity: number;
  total_value: number;
}

export interface SuperRankings {
  clients: ClientRank[];
  stores: StoreRank[];
}

export interface ClientRankings {
  stores: StoreRank[];
}

export interface SupplierQuantity {
  supplier_id: string;
  supplier_name: string;
  quantity: number;
}

export interface StoreQuantity {
  store_id: string;
  store_name: string;
  total_quantity: number;
  by_supplier: SupplierQuantity[];
}

export interface OrderOverview {
  date: string;
  total_quantity: number;
  by_store: StoreQuantity[];
  by_supplier: SupplierQuantity[];
}

// --- Derived chart types (computed on frontend) ---

export interface DashboardQuantityMetric {
  unit: DashboardUnitKind;
  quantity: number;
}

export interface ProductVolume {
  productName: string;
  quantities: DashboardQuantityBuckets;
  totalValue: number;
}

export interface SupplierValue {
  supplierName: string;
  totalValue: number;
  commission: number;
}

export interface ProductSupplierBreakdown {
  supplierName: string;
  quantities: DashboardQuantityBuckets;
  totalValue: number;
  avgPricePerBox: number;
}

export interface ProductStoreBreakdown {
  storeName: string;
  quantities: DashboardQuantityBuckets;
  totalValue?: number;
}

export interface ProductDetail {
  productName: string;
  quantities: DashboardQuantityBuckets;
  totalValue: number;
  minPricePerBox: number;
  maxPricePerBox: number;
  bySupplier: ProductSupplierBreakdown[];
  byStore: ProductStoreBreakdown[];
}

export interface DateRange {
  from: string;
  to: string;
}

export interface SupplierProductStoreBreakdown {
  storeName: string;
  quantities: DashboardQuantityBuckets;
  totalValue: number; // cents
}

export interface SupplierProductDetail {
  productName: string;
  quantities: DashboardQuantityBuckets;
  totalValue: number; // cents
  byStore: SupplierProductStoreBreakdown[];
}

// --- Dashboard v2 role contracts ---

export const DASHBOARD_CONTRACT_VERSION = 2 as const;

export type DashboardRole =
  | "super"
  | "admin_helper"
  | "client"
  | "client_store"
  | "supplier"
  | "checker"
  | "overviewer";

export type DashboardPeriodKind = "today" | "week" | "month" | "custom" | "all";

export type DashboardHistoryUnit = "day" | "week" | "month" | "period";
export type DashboardProjectionStatus = "fresh" | "stale" | "not_ready";
export type DashboardAvailability = "available" | "unavailable" | "error";
export type DashboardMetricUnit = "cents" | "count" | "percent" | "quantity";
export type DashboardComparisonDirection = "up" | "down" | "same";
export type DashboardFinancialAnalyticsLoadState =
  | "not_started"
  | "in_progress"
  | "concluded";
export type DashboardOperationalAnalyticsLoadState =
  | "not_started"
  | "in_progress"
  | "done";
export type DashboardCurrentLoadState = "not_started" | "in_progress";
export type DashboardDetailLoadState =
  | "not_started"
  | "in_progress"
  | "done"
  | "unknown";
declare const dashboardCentsBrand: unique symbol;
export type DashboardCents = number & {
  readonly [dashboardCentsBrand]: "DashboardCents";
};

export const parseDashboardCents = (value: unknown): DashboardCents => {
  if (typeof value !== "number" || !Number.isSafeInteger(value) || value < 0) {
    throw new TypeError("dashboard cents must be a nonnegative safe integer");
  }

  return value as DashboardCents;
};
export type DashboardDecimal = `${number}` | `+${number}`;
export type DashboardISODate = string;
export type DashboardISODateTime = string;
export type DashboardEntityID = string;

export interface DashboardMetricComparison {
  direction: DashboardComparisonDirection;
  percentage: DashboardDecimal;
}

export interface DashboardAvailableMetric<
  Value,
  Unit extends DashboardMetricUnit,
> {
  status: "available";
  unit: Unit;
  value: Value;
  comparison?: DashboardMetricComparison;
  reason_code?: never;
}

export interface DashboardUnavailableMetric<Unit extends DashboardMetricUnit> {
  status: "unavailable" | "error";
  unit: Unit;
  reason_code?: string;
  value?: never;
  comparison?: never;
}

export type DashboardMetric<
  Value,
  Unit extends DashboardMetricUnit = DashboardMetricUnit,
> = DashboardAvailableMetric<Value, Unit> | DashboardUnavailableMetric<Unit>;

export type DashboardMoneyMetric = DashboardMetric<DashboardCents, "cents">;
export type DashboardCountMetric = DashboardMetric<number, "count">;
export type DashboardPercentMetric = DashboardMetric<
  DashboardDecimal,
  "percent"
>;

export interface DashboardAvailableOperationalCountMetric {
  status: "available";
  unit: "count";
  count: number;
  comparison?: DashboardMetricComparison;
  reason_code?: never;
}

export interface DashboardUnavailableOperationalCountMetric {
  status: "unavailable" | "error";
  unit: "count";
  reason_code?: string;
  count?: never;
  comparison?: never;
}

export type DashboardOperationalCountMetric =
  | DashboardAvailableOperationalCountMetric
  | DashboardUnavailableOperationalCountMetric;

export interface DashboardUnitQuantity {
  unit: DashboardUnitKind;
  quantity: DashboardDecimal;
}

export interface DashboardSupplierRankingOutcomes {
  decision_count: number;
  substituted_count: number;
  returned_count: number;
}

export interface DashboardAvailableOperationalQuantityMetric {
  status: "available";
  unit: "quantity";
  quantities: DashboardUnitQuantity[];
  reason_code?: never;
}

export interface DashboardUnavailableOperationalQuantityMetric {
  status: "unavailable" | "error";
  unit: "quantity";
  quantities: [];
  reason_code?: string;
}

export type DashboardOperationalQuantityMetric =
  | DashboardAvailableOperationalQuantityMetric
  | DashboardUnavailableOperationalQuantityMetric;

export type DashboardSectionName =
  | "load_situation"
  | "sales_evolution"
  | "sales_heatmap"
  | "purchase_evolution"
  | "best_clients"
  | "best_stores"
  | "supplier_sales"
  | "supplier_quality"
  | "supplier_impact"
  | "supplier_performance"
  | "leading_products"
  | "quality_outcomes"
  | "good_prices"
  | "category_spend"
  | "purchased_quantities"
  | "stores"
  | "quantity_sold"
  | "stores_served"
  | "checker_highlights"
  | "divergence_outcomes"
  | "checkin_evolution"
  | "open_loads"
  | "current_loads"
  | "oldest_open"
  | "actions";

export interface DashboardSectionStatus {
  section: DashboardSectionName;
  status: DashboardAvailability;
  reason_code?: string;
}

interface DashboardPeriodMetadataBase {
  to: DashboardISODate;
  requested_history: 4 | 8 | 12;
  available_history: number;
}

export interface DashboardComparablePeriodMetadata extends DashboardPeriodMetadataBase {
  kind: Exclude<DashboardPeriodKind, "all">;
  from: DashboardISODate;
  comparison_from: DashboardISODate;
  comparison_to: DashboardISODate;
  comparison_basis_from: DashboardISODate;
  comparison_basis_to: DashboardISODate;
  comparison_available: true;
  history_unit: DashboardHistoryUnit;
}

export interface DashboardAllPeriodMetadata extends DashboardPeriodMetadataBase {
  kind: "all";
  from?: DashboardISODate;
  comparison_from?: never;
  comparison_to?: never;
  comparison_basis_from?: never;
  comparison_basis_to?: never;
  comparison_available: false;
  history_unit: "month";
}

export type DashboardPeriodMetadata =
  | DashboardComparablePeriodMetadata
  | DashboardAllPeriodMetadata;

export interface DashboardAnalyticsMetadata<Role extends DashboardRole> {
  role: Role;
  selected_store_id?: Role extends "client_store" ? string : never;
  period: DashboardPeriodMetadata;
  generated_at: DashboardISODateTime;
  data_as_of?: DashboardISODateTime;
  projection_status: DashboardProjectionStatus;
  section_status?: DashboardSectionStatus[];
}

export interface DashboardOperationsMetadata<Role extends DashboardRole> {
  role: Role;
  selected_store_id?: Role extends "client_store" ? string : never;
  generated_at: DashboardISODateTime;
  data_as_of?: DashboardISODateTime;
  projection_status: DashboardProjectionStatus;
  section_status?: DashboardSectionStatus[];
}

export interface DashboardMoneySeriesPoint {
  from: DashboardISODate;
  to: DashboardISODate;
  value_cents: DashboardCents;
}

export interface DashboardSalesHeatmapCell {
  week_of_month: number;
  weekday: number;
  total_value_cents: DashboardCents;
  days_with_orders: number;
}

export interface DashboardCountSeriesPoint {
  from: DashboardISODate;
  to: DashboardISODate;
  count: number;
}

export interface DashboardFinancialLoadState {
  state: DashboardFinancialAnalyticsLoadState;
  count: number;
  percentage: DashboardDecimal;
  total_value_cents: DashboardCents;
}

export interface DashboardFinancialLoadSituation {
  total: number;
  states: DashboardFinancialLoadState[];
}

export interface DashboardOperationalLoadState {
  state: DashboardOperationalAnalyticsLoadState;
  count: number;
  percentage: DashboardDecimal;
}

export interface DashboardOperationalLoadSituation {
  total: number;
  states: DashboardOperationalLoadState[];
}

export interface DashboardMoneyRankingItem {
  id?: DashboardEntityID;
  name: string;
  value_cents: DashboardCents;
}

export interface DashboardSupplierQualityItem {
  supplier_id: DashboardEntityID;
  supplier_name: string;
  return_rate: DashboardDecimal;
  impact_cents: DashboardCents;
  supplied_orders: number;
}

export interface DashboardQuantityRankingItem {
  id?: DashboardEntityID;
  name: string;
  quantities: DashboardUnitQuantity[];
}

export interface DashboardQualityOutcomes {
  decision_count: number;
  substituted_count: number;
  returned_count: number;
  divergence_count: number;
}

export interface DashboardSupplierQualityOutcomes {
  decision_count: number;
  substituted_count: number;
  returned_count: number;
  return_impact_cents: DashboardCents;
}

export interface DashboardGoodPriceItem {
  product_id: DashboardEntityID;
  product_name: string;
  supplier_id: DashboardEntityID;
  supplier_name: string;
  store_id: DashboardEntityID;
  store_name: string;
  unit: string;
  previous_price_cents: DashboardCents;
  current_price_cents: DashboardCents;
  percentage_drop: DashboardDecimal;
}

export interface DashboardCategorySpendItem {
  category_name: string;
  value_cents: DashboardCents;
}

export interface DashboardFinancialLoadPreview {
  order_id: DashboardEntityID;
  label: string;
  state: DashboardCurrentLoadState;
  value_cents: DashboardCents;
}

export interface DashboardOperationalLoadPreview {
  order_id: DashboardEntityID;
  label: string;
  state: DashboardCurrentLoadState;
  created_at: DashboardISODateTime;
}

export interface DashboardCheckerHighlight {
  checker_id: DashboardEntityID;
  checker_name: string;
  checked_items: number;
  divergences: number;
  concluded_loads: number;
}

export interface DashboardActionPreview {
  id: DashboardEntityID;
  kind: string;
  label: string;
  priority: string;
}

export interface SuperDashboardMetrics {
  sales: DashboardMoneyMetric;
  open_value: DashboardMoneyMetric;
  orders: DashboardCountMetric;
  average_ticket: DashboardMoneyMetric;
  completed_loads: DashboardCountMetric;
}

export interface SuperDashboardSections {
  load_situation: DashboardFinancialLoadSituation;
  sales_evolution: DashboardMoneySeriesPoint[];
  sales_heatmap?: DashboardSalesHeatmapCell[];
  best_clients: DashboardMoneyRankingItem[];
  best_stores?: (DashboardMoneyRankingItem & { id: DashboardEntityID })[];
  supplier_sales?: (DashboardMoneyRankingItem & { id: DashboardEntityID })[];
  supplier_quality: DashboardSupplierQualityItem[];
  supplier_impact: DashboardSupplierQualityItem[];
  leading_products: DashboardQuantityRankingItem[];
  category_spend: DashboardCategorySpendItem[];
  purchased_quantities: DashboardUnitQuantity[];
  quality_outcomes: DashboardQualityOutcomes;
}

export interface SuperDashboardAnalytics extends DashboardAnalyticsMetadata<"super"> {
  metrics: SuperDashboardMetrics;
  sections: SuperDashboardSections;
}

export interface ClientDashboardMetrics {
  purchases: DashboardMoneyMetric;
  open_value: DashboardMoneyMetric;
  orders: DashboardCountMetric;
  no_occurrence_rate: DashboardPercentMetric;
}

export interface ClientDashboardSections {
  good_prices: DashboardGoodPriceItem[];
  category_spend: DashboardCategorySpendItem[];
  purchased_quantities: DashboardUnitQuantity[];
  leading_products: DashboardQuantityRankingItem[];
  supplier_performance: DashboardSupplierQualityItem[];
  supplier_impact: DashboardSupplierQualityItem[];
  stores: DashboardMoneyRankingItem[];
  purchase_evolution: DashboardMoneySeriesPoint[];
  load_situation: DashboardFinancialLoadSituation;
  quality_outcomes: DashboardQualityOutcomes;
}

export interface ClientStoreDashboardSections extends Omit<
  ClientDashboardSections,
  "stores"
> {
  stores?: never;
}

export interface ClientDashboardAnalytics extends DashboardAnalyticsMetadata<"client"> {
  metrics: ClientDashboardMetrics;
  sections: ClientDashboardSections;
}

export interface ClientStoreDashboardAnalytics extends DashboardAnalyticsMetadata<"client_store"> {
  metrics: ClientDashboardMetrics;
  sections: ClientStoreDashboardSections;
}

export interface SupplierDashboardMetrics {
  earnings: DashboardMoneyMetric;
  open_value: DashboardMoneyMetric;
  orders: DashboardCountMetric;
  average_ticket: DashboardMoneyMetric;
}

export interface SupplierDashboardSections {
  sales_evolution: DashboardMoneySeriesPoint[];
  leading_products: DashboardQuantityRankingItem[];
  quantity_sold: DashboardUnitQuantity[];
  stores_served: DashboardMoneyRankingItem[];
  load_situation: DashboardFinancialLoadSituation;
  quality_outcomes: DashboardSupplierQualityOutcomes;
}

export interface SupplierDashboardAnalytics extends DashboardAnalyticsMetadata<"supplier"> {
  metrics: SupplierDashboardMetrics;
  sections: SupplierDashboardSections;
}

export interface DashboardOperationalMetrics {
  checked_loads: DashboardOperationalCountMetric;
  checked_items: DashboardOperationalCountMetric;
  open_loads: DashboardOperationalCountMetric;
  checked_quantities: DashboardOperationalQuantityMetric;
  divergences: DashboardOperationalCountMetric;
}

export interface DashboardOperationalSections {
  load_situation: DashboardOperationalLoadSituation;
  checker_highlights: DashboardCheckerHighlight[];
  divergence_outcomes: DashboardQualityOutcomes;
  checkin_evolution: DashboardCountSeriesPoint[];
}

export interface CheckerDashboardAnalytics extends DashboardAnalyticsMetadata<"checker"> {
  metrics: DashboardOperationalMetrics;
  sections: DashboardOperationalSections;
}

export interface OverviewerDashboardAnalytics extends DashboardAnalyticsMetadata<"overviewer"> {
  metrics: DashboardOperationalMetrics;
  sections: DashboardOperationalSections;
}

export interface AdminHelperDashboardAnalytics extends DashboardAnalyticsMetadata<"admin_helper"> {
  metrics: DashboardOperationalMetrics;
  sections: DashboardOperationalSections;
}

export interface DashboardFinancialOpenLoads {
  not_started: number;
  in_progress: number;
  total: number;
  total_value_cents: DashboardCents;
}

export interface DashboardOperationalOpenLoads {
  not_started: number;
  in_progress: number;
  total: number;
}

export interface SuperDashboardOperations extends DashboardOperationsMetadata<"super"> {
  open_loads: DashboardFinancialOpenLoads;
  actions: DashboardActionPreview[];
}

export interface ClientDashboardOperations extends DashboardOperationsMetadata<"client"> {
  open_loads: DashboardFinancialOpenLoads;
  current_loads: DashboardFinancialLoadPreview[];
}

export interface ClientStoreDashboardOperations extends DashboardOperationsMetadata<"client_store"> {
  open_loads: DashboardFinancialOpenLoads;
  current_loads: DashboardFinancialLoadPreview[];
}

export interface SupplierDashboardOperations extends DashboardOperationsMetadata<"supplier"> {
  open_loads: DashboardFinancialOpenLoads;
  current_loads: DashboardFinancialLoadPreview[];
}

export interface SupplierCurrentLoadItem {
  order_id: DashboardEntityID;
  business_date: DashboardISODate;
  state: DashboardCurrentLoadState;
  label: string;
  value_cents: DashboardCents;
  quantities: DashboardUnitQuantity[];
}

export interface SupplierCurrentLoadsPage extends Omit<
  DashboardOperationsMetadata<"supplier">,
  "section_status"
> {
  value_status: "provisional";
  items: SupplierCurrentLoadItem[];
  has_more: boolean;
  next_cursor?: string;
}

export interface CheckerDashboardOperations extends DashboardOperationsMetadata<"checker"> {
  open_loads: DashboardOperationalOpenLoads;
  oldest_open: DashboardOperationalLoadPreview[];
}

export interface OverviewerDashboardOperations extends DashboardOperationsMetadata<"overviewer"> {
  open_loads: DashboardOperationalOpenLoads;
  oldest_open: DashboardOperationalLoadPreview[];
}

export interface AdminHelperDashboardAlerts {
  unresolved: number;
  high: number;
  medium: number;
  low: number;
}

/** Deliberately exact: helper quotation progress cannot expose prices or configuration detail. */
export interface AdminHelperActiveQuotation {
  id: DashboardEntityID;
  date: DashboardISODate;
  status: "not_started" | "in_progress" | "completed";
  item_count: number;
}

export interface AdminHelperDashboardOperations extends DashboardOperationsMetadata<"admin_helper"> {
  open_loads: DashboardOperationalOpenLoads;
  oldest_open: DashboardOperationalLoadPreview[];
  alerts: AdminHelperDashboardAlerts;
  active_quotation: AdminHelperActiveQuotation | null;
}

export type DashboardAnalyticsResponse =
  | SuperDashboardAnalytics
  | ClientDashboardAnalytics
  | ClientStoreDashboardAnalytics
  | SupplierDashboardAnalytics
  | CheckerDashboardAnalytics
  | OverviewerDashboardAnalytics
  | AdminHelperDashboardAnalytics;

export type DashboardOperationsResponse =
  | SuperDashboardOperations
  | ClientDashboardOperations
  | ClientStoreDashboardOperations
  | SupplierDashboardOperations
  | CheckerDashboardOperations
  | OverviewerDashboardOperations
  | AdminHelperDashboardOperations;

export type DashboardRoleAnalytics<Role extends DashboardRole> =
  Role extends "super"
    ? SuperDashboardAnalytics
    : Role extends "client"
      ? ClientDashboardAnalytics
      : Role extends "client_store"
        ? ClientStoreDashboardAnalytics
        : Role extends "supplier"
          ? SupplierDashboardAnalytics
          : Role extends "checker"
            ? CheckerDashboardAnalytics
            : Role extends "overviewer"
              ? OverviewerDashboardAnalytics
              : AdminHelperDashboardAnalytics;

export type DashboardRoleOperations<Role extends DashboardRole> =
  Role extends "super"
    ? SuperDashboardOperations
    : Role extends "client"
      ? ClientDashboardOperations
      : Role extends "client_store"
        ? ClientStoreDashboardOperations
        : Role extends "supplier"
          ? SupplierDashboardOperations
          : Role extends "checker"
            ? CheckerDashboardOperations
            : Role extends "overviewer"
              ? OverviewerDashboardOperations
              : AdminHelperDashboardOperations;

export type DashboardRankingKind =
  | "clients"
  | "suppliers"
  | "products"
  | "stores"
  | "checkers";

export type DashboardRankingSort =
  | "value"
  | "return_rate"
  | "financial_impact"
  | "checked_items";

export interface DashboardRankingMoneyItem {
  id: DashboardEntityID;
  name: string;
  value_cents: DashboardCents;
  quantities: DashboardUnitQuantity[];
}

export interface DashboardSupplierRankingItem {
  id: DashboardEntityID;
  name: string;
  supplied_count: number;
  return_count: number;
  return_rate: DashboardDecimal;
  financial_impact_cents: DashboardCents;
  outcomes: DashboardSupplierRankingOutcomes;
}

export interface DashboardProductRankingItem {
  id: DashboardEntityID;
  name: string;
  value_cents: DashboardCents;
  quantities: DashboardUnitQuantity[];
}

export interface DashboardCheckerRankingItem {
  checker_id: DashboardEntityID;
  checker_name: string;
  checked_items: number;
  divergences: number;
  concluded_loads: number;
}

type DashboardRankingPagination =
  | { has_more: true; next_cursor: string }
  | { has_more: false; next_cursor?: never };

export type DashboardClientRankingPage = {
  kind: "clients";
  sort: "value";
  items: DashboardRankingMoneyItem[];
} & DashboardRankingPagination;

export type DashboardSupplierRankingPage = {
  kind: "suppliers";
  sort: "return_rate" | "financial_impact";
  items: DashboardSupplierRankingItem[];
} & DashboardRankingPagination;

export type DashboardSupplierSalesRankingPage = {
  kind: "suppliers";
  sort: "value";
  items: Pick<DashboardRankingMoneyItem, "id" | "name" | "value_cents">[];
} & DashboardRankingPagination;

export type DashboardProductRankingPage = {
  kind: "products";
  sort: "value";
  items: DashboardProductRankingItem[];
} & DashboardRankingPagination;

export type DashboardStoreRankingPage = {
  kind: "stores";
  sort: "value";
  items: DashboardRankingMoneyItem[];
} & DashboardRankingPagination;

export type DashboardCheckerRankingPage = {
  kind: "checkers";
  sort: "checked_items";
  items: DashboardCheckerRankingItem[];
} & DashboardRankingPagination;

export type DashboardRankingResponse =
  | DashboardClientRankingPage
  | DashboardSupplierRankingPage
  | DashboardSupplierSalesRankingPage
  | DashboardProductRankingPage
  | DashboardStoreRankingPage
  | DashboardCheckerRankingPage;

export type DashboardDetailKind =
  | "client"
  | "supplier"
  | "product"
  | "store"
  | "load"
  | "checker";

export type DashboardFinancialDetailKind = Exclude<
  DashboardDetailKind,
  "load" | "checker"
>;

export type DashboardDetailEntity<
  Kind extends DashboardDetailKind = DashboardDetailKind,
> = {
  id: DashboardEntityID;
  name: string;
  kind: Kind;
} & (Kind extends "load"
  ? { state: DashboardDetailLoadState }
  : { state?: never });

export interface DashboardFinancialDetailItem {
  order_id: DashboardEntityID;
  business_date: DashboardISODate;
  state: DashboardDetailLoadState;
  label: string;
  value_cents: DashboardCents;
  quantities: DashboardUnitQuantity[];
}

export interface DashboardOperationalDetailItem {
  order_id: DashboardEntityID;
  business_date: DashboardISODate;
  state: DashboardDetailLoadState;
  checked_items: number;
  divergences: number;
  checked_quantities: DashboardUnitQuantity[];
}

export interface DashboardLoadLine {
  line_id: DashboardEntityID;
  product_id: DashboardEntityID;
  product_name: string;
  unit: string;
  expected_quantity: DashboardDecimal;
  received_quantity: DashboardDecimal;
  status: string;
}

export interface DashboardFinancialLoadLine extends DashboardLoadLine {
  supplier_id: DashboardEntityID;
  supplier_name: string;
  unit_price_cents: DashboardCents;
  value_cents: DashboardCents;
}

interface DashboardDetailPageBase<Entity, Item> {
  entity: Entity;
  items: Item[];
}

export type DashboardDetailPage<Entity, Item> = DashboardDetailPageBase<
  Entity,
  Item
> &
  (
    | { has_more: true; next_cursor: string }
    | { has_more: false; next_cursor?: never }
  );

export type DashboardFinancialDetailPage<
  Kind extends DashboardFinancialDetailKind = DashboardFinancialDetailKind,
> = DashboardDetailPage<
  DashboardDetailEntity<Kind>,
  DashboardFinancialDetailItem
>;

export type DashboardOperationalDetailPage = DashboardDetailPage<
  DashboardDetailEntity<"checker">,
  DashboardOperationalDetailItem
>;

export type DashboardFinancialLoadDetailPage = DashboardDetailPage<
  DashboardDetailEntity<"load">,
  DashboardFinancialLoadLine
>;

export type DashboardOperationalLoadDetailPage = DashboardDetailPage<
  DashboardDetailEntity<"load">,
  DashboardLoadLine
>;

export type DashboardFinancialDetailResponse =
  | DashboardFinancialDetailPage
  | DashboardFinancialLoadDetailPage;

export type DashboardOperationalDetailResponse =
  | DashboardOperationalDetailPage
  | DashboardOperationalLoadDetailPage;

export type DashboardDetailResponse =
  | DashboardFinancialDetailResponse
  | DashboardOperationalDetailResponse;

export type DashboardRoleDetailResponse<
  Role extends DashboardRole,
  Kind extends DashboardDetailKind,
> = Role extends "checker" | "overviewer"
  ? Kind extends "load"
    ? DashboardOperationalLoadDetailPage
    : Kind extends "checker"
      ? DashboardOperationalDetailPage
      : never
  : Role extends "admin_helper"
    ? never
    : Role extends "super"
      ? Kind extends "load"
        ? DashboardFinancialLoadDetailPage
        : Kind extends "checker"
          ? DashboardOperationalDetailPage
          : Kind extends DashboardFinancialDetailKind
            ? DashboardFinancialDetailPage<Kind>
            : never
      : Kind extends "checker"
        ? never
        : Kind extends "load"
          ? DashboardFinancialLoadDetailPage
          : Kind extends DashboardFinancialDetailKind
            ? DashboardFinancialDetailPage<Kind>
            : never;
